<template>
  <DefaultCrudView
    @create-new="newAcl"
    :breadcrumbsItems="breadcrumbs"
    :actions="crudActions"
    containerLarge
  >
    <template slot="content">

      <v-data-table
        :headers="headers"
        :items="acls"
        class="elevation-1 compact-table"
        :loading="loading"
        :options.sync="options"
        :server-items-length="itemsLength"
        :footer-props="$defaultDataTableFooterProps"
      >
        <template v-slot:[`item.owner`]="{ item }">
          <v-chip small color="primary" dark v-if="item.user_group_id">
            <v-icon x-small left>mdi-account-multiple</v-icon>
            {{ getGroupTitle(item.user_group_id) }}
          </v-chip>
          <v-chip small color="blue-grey darken-2" dark v-else-if="item.users_id">
            <v-icon x-small left>mdi-account</v-icon>
            {{ getUserName(item.users_id) }}
          </v-chip>
          <span v-else>—</span>
        </template>

        <template v-slot:[`item.access_level`]="{ item }">
          <v-chip
            v-for="perm in getPermissions(item.access_level)"
            :key="perm.key"
            x-small
            class="mr-1 mb-1"
            :color="perm.color"
            dark
          >
            {{ perm.label }}
          </v-chip>
        </template>

        <template v-slot:[`item.actions`]="{ item }">
          <v-menu offset-y left>
            <template v-slot:activator="{ on, attrs }">
              <v-btn icon v-bind="attrs" v-on="on">
                <v-icon>mdi-dots-horizontal</v-icon>
              </v-btn>
            </template>
            <v-list dense>
              <v-list-item @click="editAcl(item)">
                <v-list-item-icon>
                  <v-icon medium>mdi-pencil</v-icon>
                </v-list-item-icon>
                <v-list-item-title>{{ $t('edit') }}</v-list-item-title>
              </v-list-item>

              <v-divider></v-divider>

              <v-list-item @click="confirmDeleteAcl(item)">
                <v-list-item-icon>
                  <v-icon medium color="error">mdi-delete</v-icon>
                </v-list-item-icon>
                <v-list-item-title class="error--text">{{ $t('delete') }}</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </template>

      </v-data-table>

      <!-- Create / Edit Modal -->
      <DefaultModal :title="getModalTitle()" ref="modal" @submit="save">
        <template v-slot:content>
          <v-form ref="aclForm">

            <!-- ── Create mode: single-select owner ── -->
            <template v-if="!selectedAcl">
              <v-autocomplete
                v-model="formData.selectedOwner"
                :items="normalizedUsersAndGroups"
                :label="$t('acl_owner')"
                :loading="loadingUsersGroupsList"
                :filter="filterOwner"
                item-text="name"
                item-value="id"
                :rules="[v => !!v || $t('field_required')]"
                outlined
                dense
                clearable
                class="mb-2"
              >
                <!-- selected chip -->
                <template v-slot:selection="data">
                  <v-chip
                    v-bind="data.attrs"
                    :input-value="data.selected"
                    color="primary"
                    dark
                    small
                  >
                    <v-avatar left class="owner-avatar">
                      <img v-if="data.item.avatar_url" :src="data.item.avatar_url" />
                      <v-icon color="white" small v-else-if="data.item.isGroup">mdi-account-multiple</v-icon>
                      <span v-else class="white--text text-uppercase font-weight-medium caption">{{ data.item.name[0] }}</span>
                    </v-avatar>
                    {{ data.item.name }}
                  </v-chip>
                </template>

                <!-- dropdown item -->
                <template v-slot:item="data">
                  <v-list-item-avatar class="owner-list-avatar" :class="data.item.isGroup ? 'group-bg' : 'avatar-bg'">
                    <img v-if="data.item.avatar_url" :src="data.item.avatar_url" />
                    <v-icon color="white" v-else-if="data.item.isGroup">mdi-account-multiple</v-icon>
                    <span v-else class="white--text text-uppercase font-weight-medium">{{ data.item.name[0] }}</span>
                  </v-list-item-avatar>

                  <v-list-item-content>
                    <v-list-item-title>{{ data.item.name }}</v-list-item-title>
                    <v-list-item-subtitle v-if="data.item.email">{{ data.item.email }}</v-list-item-subtitle>
                    <v-list-item-subtitle v-if="data.item.isGroup" class="caption">{{ $t('user_group') }}</v-list-item-subtitle>
                  </v-list-item-content>
                </template>
              </v-autocomplete>
            </template>

            <!-- ── Edit mode: show owner read-only ── -->
            <template v-else>
              <div class="mb-4">
                <div class="text-caption grey--text mb-1">{{ $t('acl_owner') }}</div>
                <v-chip color="primary" dark>
                  <v-avatar left class="owner-avatar">
                    <img v-if="selectedOwnerItem && selectedOwnerItem.avatar_url" :src="selectedOwnerItem.avatar_url" />
                    <v-icon color="white" small v-else-if="selectedAcl.user_group_id">mdi-account-multiple</v-icon>
                    <span v-else class="white--text text-uppercase font-weight-medium caption">
                      {{ (selectedOwnerItem && selectedOwnerItem.name) ? selectedOwnerItem.name[0] : '?' }}
                    </span>
                  </v-avatar>
                  <template v-if="selectedAcl.user_group_id">
                    {{ getGroupTitle(selectedAcl.user_group_id) }}
                  </template>
                  <template v-else>
                    {{ getUserName(selectedAcl.users_id) }}
                  </template>
                </v-chip>
              </div>
            </template>

            <!-- ── Access level permissions (create & edit) ── -->
            <div class="text-subtitle-2 mb-2">{{ $t('acl_access_level') }}</div>
            <v-row dense>
              <v-col cols="12" sm="6" v-for="perm in accessLevelOptions" :key="perm.key">
                <v-switch
                  v-model="formData.permissions[perm.key]"
                  :label="perm.label"
                  dense
                  hide-details
                  class="mt-0"
                ></v-switch>
              </v-col>
            </v-row>

          </v-form>
        </template>
      </DefaultModal>

      <!-- Confirm delete dialog -->
      <v-dialog v-model="deleteDialog" max-width="400">
        <v-card>
          <v-card-title class="text-h6">{{ $t('confirm_delete') }}</v-card-title>
          <v-card-text>{{ $t('acl_confirm_delete') }}</v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text @click="deleteDialog = false">{{ $t('close') }}</v-btn>
            <v-btn color="error" text :loading="deleting" @click="deleteAcl">{{ $t('delete') }}</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

    </template>
  </DefaultCrudView>
</template>

<style scoped>
  .container {
    padding: 0;
  }

  ::v-deep .compact-table .v-data-table__wrapper td,
  ::v-deep .compact-table .v-data-table__wrapper th {
    padding: 6px 8px !important;
  }

  .owner-avatar {
    background-color: rgba(255, 255, 255, 0.3) !important;
    justify-content: center;
  }

  .owner-list-avatar {
    justify-content: center;
  }

  .avatar-bg {
    background-color: var(--v-primary-base) !important;
  }

  .group-bg {
    background-color: var(--v-primary-base) !important;
  }
</style>

<script>
import { mapActions } from 'vuex'

import DefaultCrudView from '@/components/DefaultCrudView.vue'
import DefaultModal from '@/components/DefaultModal.vue'

import AppActions from '@/store/app/actions-types'
import UserActions from '@/store/core/user/actions-types'
import GroupActions from '@/store/core/group/actions-types'

import AclAPI from '@/services/api/business/acl'

const ACCESS_LEVELS = {
  READ_CONFIG:  1,
  READ_SERVICE: 2,
  EDIT:         4,
  PRICE_READ:   8,
  PRICE_WRITE:  16,
}

export default {
  name: 'CustomerAcls',

  components: {
    DefaultCrudView,
    DefaultModal,
  },

  computed: {
    customerId() {
      return this.$route.params.customerId
    },

    customerName() {
      return this.$route.query.customerName || this.customerId
    },

    breadcrumbs() {
      return [
        {
          text: this.$t('settings'),
          prependIcon: 'mdi-cog-outline',
          iconColor: 'primary',
          to: { name: 'DeliverableSettings' },
          disabled: false,
          exact: true,
          ripple: true,
        },
        {
          text: this.$t('customers'),
          prependIcon: 'mdi-account-group',
          to: { name: 'BusinessCustomers' },
          disabled: false,
          exact: true,
          ripple: true,
        },
        {
          text: this.$t('customer_acls') + (this.customerName ? ': ' + this.customerName : ''),
          prependIcon: 'mdi-shield-key-outline',
        },
      ]
    },

    accessLevelOptions() {
      return [
        { key: 'read_config',  label: this.$t('acl_read_config'),  value: ACCESS_LEVELS.READ_CONFIG,  color: 'blue'   },
        { key: 'read_service', label: this.$t('acl_read_service'), value: ACCESS_LEVELS.READ_SERVICE, color: 'cyan'   },
        { key: 'edit',         label: this.$t('acl_edit'),         value: ACCESS_LEVELS.EDIT,         color: 'orange' },
        { key: 'price_read',   label: this.$t('acl_price_read'),   value: ACCESS_LEVELS.PRICE_READ,   color: 'green'  },
        { key: 'price_write',  label: this.$t('acl_price_write'),  value: ACCESS_LEVELS.PRICE_WRITE,  color: 'red'    },
      ]
    },

    // Combined normalized list of users + groups for the autocomplete
    normalizedUsersAndGroups() {
      return this.usersAndGroups.reduce((result, el) => {
        result.push({
          id:         el.users_id ? `users-${el.users_id}` : `group-${el.user_group_id}`,
          idRaw:      el.users_id || el.user_group_id,
          name:       el.name || el.group_title,
          isUser:     !!el.users_id,
          isGroup:    !!el.user_group_id,
          email:      el.email || null,
          avatar_url: el.avatar_url || null,
        })
        return result
      }, [])
    },

    // Resolve the owner item object for the edit-mode read-only chip
    selectedOwnerItem() {
      if (!this.selectedAcl) return null
      if (this.selectedAcl.user_group_id) {
        return this.normalizedUsersAndGroups.find(el => el.isGroup && el.idRaw == this.selectedAcl.user_group_id) || null
      }
      return this.normalizedUsersAndGroups.find(el => el.isUser && el.idRaw == this.selectedAcl.users_id) || null
    },
  },

  data() {
    return {
      loading: true,
      acls: [],
      options: {
        sortBy: ['acl_id'],
        sortDesc: [false],
      },
      itemsLength: 0,
      crudActions: [
        {
          text: 'create_acl',
          eventToEmit: 'create-new',
        },
      ],
      selectedAcl: null,
      deleteDialog: false,
      deleting: false,
      aclToDelete: null,
      formData: this.emptyFormData(),

      // Users & groups for autocomplete
      loadingUsersGroupsList: false,
      usersAndGroups: [],

      headers: [
        { text: this.$t('acl_id'),           value: 'acl_id',       sortable: true  },
        { text: this.$t('acl_owner'),         value: 'owner',        sortable: false },
        { text: this.$t('acl_access_level'),  value: 'access_level', sortable: false },
        { text: this.$t('actions'),           value: 'actions',      sortable: false },
      ],
    }
  },

  mounted() {
    this.selectedAcl = null
    this.loadUsersAndGroups()
  },

  watch: {
    options: {
      handler() {
        this.reloadData()
      },
      deep: true,
    },
  },

  methods: {
    ...mapActions('app', [
      AppActions.OPEN_APP_SUCCESS_MESSAGE,
    ]),

    ...mapActions('core/user', [
      UserActions.GET_ALL_USERS,
    ]),

    ...mapActions('core/group', [
      GroupActions.GET_ALL_GROUPS,
    ]),

    emptyFormData() {
      return {
        selectedOwner: null,
        permissions: {
          read_config:  false,
          read_service: false,
          edit:         false,
          price_read:   false,
          price_write:  false,
        },
      }
    },

    async loadUsersAndGroups() {
      this.loadingUsersGroupsList = true
      this.usersAndGroups = []

      try {
        const usersPromise  = this[UserActions.GET_ALL_USERS]({ limit: 9999, 'filter[active]': true })
        const groupsPromise = this[GroupActions.GET_ALL_GROUPS]({ limit: 9999, 'filter[active]': true })

        usersPromise.then(result  => { this.usersAndGroups = [...this.usersAndGroups, ...(result.items || [])] })
        groupsPromise.then(result => { this.usersAndGroups = [...this.usersAndGroups, ...(result.items || [])] })

        await Promise.all([usersPromise, groupsPromise])
      } catch (e) {
        console.error('Error loading users/groups:', e)
      } finally {
        this.loadingUsersGroupsList = false
      }
    },

    filterOwner(item, queryText) {
      const name  = (item.name  || '').toLowerCase()
      const email = (item.email || '').toLowerCase()
      const q     = queryText.toLowerCase()
      return name.includes(q) || email.includes(q)
    },

    computeAccessLevel(permissions) {
      let level = 0
      if (permissions.read_config)  level |= ACCESS_LEVELS.READ_CONFIG
      if (permissions.read_service) level |= ACCESS_LEVELS.READ_SERVICE
      if (permissions.edit)         level |= ACCESS_LEVELS.EDIT
      if (permissions.price_read)   level |= ACCESS_LEVELS.PRICE_READ
      if (permissions.price_write)  level |= ACCESS_LEVELS.PRICE_WRITE
      return level
    },

    permissionsFromAccessLevel(accessLevel) {
      return {
        read_config:  (accessLevel & ACCESS_LEVELS.READ_CONFIG)  !== 0,
        read_service: (accessLevel & ACCESS_LEVELS.READ_SERVICE) !== 0,
        edit:         (accessLevel & ACCESS_LEVELS.EDIT)         !== 0,
        price_read:   (accessLevel & ACCESS_LEVELS.PRICE_READ)   !== 0,
        price_write:  (accessLevel & ACCESS_LEVELS.PRICE_WRITE)  !== 0,
      }
    },

    getPermissions(accessLevel) {
      const flagMap = {
        read_config:  ACCESS_LEVELS.READ_CONFIG,
        read_service: ACCESS_LEVELS.READ_SERVICE,
        edit:         ACCESS_LEVELS.EDIT,
        price_read:   ACCESS_LEVELS.PRICE_READ,
        price_write:  ACCESS_LEVELS.PRICE_WRITE,
      }
      return this.accessLevelOptions
        .filter(opt => accessLevel & flagMap[opt.key])
        .map(opt => ({ key: opt.key, label: opt.label, color: opt.color }))
    },

    getUserName(userId) {
      const found = this.usersAndGroups.find(el => el.users_id == userId)
      return found ? (found.name || `${this.$t('user')} ${userId}`) : `${this.$t('user')} ${userId}`
    },

    getGroupTitle(groupId) {
      const found = this.usersAndGroups.find(el => el.user_group_id == groupId)
      return found ? (found.group_title || `${this.$t('user_group')} ${groupId}`) : `${this.$t('user_group')} ${groupId}`
    },

    newAcl() {
      this.selectedAcl = null
      this.formData = this.emptyFormData()
      this.$refs.modal.openModal()
    },

    editAcl(acl) {
      this.selectedAcl = acl
      this.formData = {
        selectedOwner: null, // read-only in edit mode
        permissions: this.permissionsFromAccessLevel(acl.access_level),
      }
      this.$refs.modal.openModal()
    },

    save() {
      if (!this.$refs.modal) return

      this.$refs.modal.errorMessage = null
      this.$refs.modal.errorList    = []

      const accessLevel = this.computeAccessLevel(this.formData.permissions)

      let apiCall

      if (this.selectedAcl) {
        // ── Update: only access_level ──
        const aclId = this.selectedAcl.acl_id
        if (!aclId) {
          this.$refs.modal.errorMessage = this.$t('acl_id_not_found')
          return
        }
        apiCall = AclAPI.updateAcl(aclId, { access_level: accessLevel })

      } else {
        // ── Create: owner + access_level ──
        if (!this.formData.selectedOwner) {
          this.$refs.modal.errorMessage = this.$t('acl_owner_required')
          this.$refs.modal.submitting = false
          return
        }

        const ownerEntry = this.normalizedUsersAndGroups.find(el => el.id === this.formData.selectedOwner)
        if (!ownerEntry) {
          this.$refs.modal.errorMessage = this.$t('acl_owner_required')
          this.$refs.modal.submitting = false
          return
        }

        apiCall = AclAPI.createAcl({
          customer_id:   parseInt(this.customerId),
          user_group_id: ownerEntry.isGroup ? ownerEntry.idRaw : null,
          users_id:      ownerEntry.isUser  ? ownerEntry.idRaw : null,
          access_level:  accessLevel,
        })
      }

      apiCall
        .then(() => {
          const message = this.selectedAcl
            ? this.$t('acl_updated_successfully')
            : this.$t('acl_created_successfully')
          this[AppActions.OPEN_APP_SUCCESS_MESSAGE](message)
          this.$refs.modal.closeModal()
          this.reloadData()
        })
        .catch(error => {
          this.$refs.modal.submitting = false
          if (error.response && error.response.data) {
            this.$refs.modal.errorMessage = error.response.data.message || this.$t('error')
          }
        })
    },

    confirmDeleteAcl(acl) {
      this.aclToDelete = acl
      this.deleteDialog = true
    },

    deleteAcl() {
      if (!this.aclToDelete) return
      this.deleting = true
      AclAPI.deleteAcl(this.aclToDelete.acl_id)
        .then(() => {
          this[AppActions.OPEN_APP_SUCCESS_MESSAGE](this.$t('acl_deleted_successfully'))
          this.deleteDialog  = false
          this.aclToDelete   = null
          this.reloadData()
        })
        .catch(error => { console.error('Error deleting ACL:', error) })
        .finally(() => { this.deleting = false })
    },

    reloadData() {
      const options = { ...this.$route.query }

      if (this.options.sortBy.length > 0)
        options.order = this.options.sortBy[0]

      if (this.options.sortDesc.length > 0 && this.options.sortDesc[0])
        options.direction = 'DESC'

      options.limit  = this.options.itemsPerPage
      options.offset = this.options.itemsPerPage * (this.options.page - 1)

      delete options.customerName

      this.loading = true
      AclAPI.getAclsByCustomer(this.customerId, options)
        .then(result => {
          this.acls        = result.data.items || []
          this.itemsLength = result.data.total  || 0
          this.loading     = false
        })
        .catch(() => { this.loading = false })
    },

    getModalTitle() {
      return this.selectedAcl ? this.$t('edit_acl') : this.$t('new_acl')
    },
  },
}
</script>
